import { LayoutGrid, PieChart, TableProperties } from '@alchemer/ui/icons';
import { Link } from '@tanstack/react-router';
import { useContext } from 'react';
import { PrerenderedAppEmbedContext } from '@/components/Thoughtspot/PreRenderedAppEmbed';

import { PAGE_IDS } from '@/constants/pages';
import { useSamePageNavigation } from '@/hooks/useSamePageNavigation';
import { PAGE_ID } from '@/types';
import { useFlag } from '@alchemer/unleash';
import { FEATURE_FLAGS } from '@/config/features';

const iconMap = {
  chart: PieChart,
  grid: LayoutGrid,
  table: TableProperties,
} as const;

export type IconType = keyof typeof iconMap;

interface CreateItemProps {
  title: string;
  description: string;
  link: PAGE_ID | string;
  icon: IconType;
  toggleCallback: () => void;
}

export default function CreateItem({ title, description, icon, link, toggleCallback }: CreateItemProps) {
  const { isCurrentPage } = useSamePageNavigation();
  const { setIsVisible } = useContext(PrerenderedAppEmbedContext);
  const isDatasetsV2 = useFlag(FEATURE_FLAGS.DATASETS_2_0);

  const classes = 'group hover:bg-background-100 flex items-center gap-3 px-3 py-1.5 rounded-xs transition-colors';
  const IconComponent = iconMap[icon];
  const content = (
    <>
      <IconComponent
        className="text-muted-foreground group-hover:text-accent-foreground shrink-0"
        height={20}
        width={20}
      />
      <div>
        <div className="text-accent-foreground text-sm font-semibold">{title}</div>
        <div className="text-muted-foreground group-hover:text-accent-foreground text-[10px] font-normal">
          {description}
        </div>
      </div>
    </>
  );

  const _handleSourceClick = () => {
    toggleCallback();
    setIsVisible(false);
  };

  const getToRoute = (link: PAGE_ID) => {
    if (link === PAGE_IDS.Datasets && isDatasetsV2) {
      return `/dashboard/${PAGE_IDS.Datasets}`;
    }
    if (link === PAGE_IDS.Sources || link === PAGE_IDS.Datasets) {
      return '/dashboard/data-source/$tab';
    }
    return '/dashboard/$page';
  };

  if (Object.values(PAGE_IDS).includes(link as PAGE_ID)) {
    return (
      <Link
        to={getToRoute(link as PAGE_ID)}
        params={{ page: link, tab: link }}
        className={classes}
        search={isCurrentPage('/dashboard/$page', { page: link }) ? { reload: new Date().getTime() } : {}}
        onClick={toggleCallback}
      >
        {content}
      </Link>
    );
  }
}
